import React from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Mode, Player } from '@par/shared/common/types';
import { Game } from '@par/shared/types/game';
import {
  CATEGORY_SETS_LABEL,
  MODE_DISPLAY_NAME,
} from '@par/shared/common/constants';
import { MAX_PLAYERS_IN_TEAMS } from '../../../main/LobbyTable';

type PropTypes = {
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  openGames: Game[];
  profileId: string;
};

export const OpenGames = ({
  handleJoinGameClick,
  handleLeaveGameClick,
  openGames,
  profileId,
}: PropTypes) => {
  const theme = useTheme();

  const getRatingNextToUsername = (
    mode: Mode,
    gameOver: boolean,
    player: Player
  ) => {
    const formatRating = (rating: number) => {
      const rounded = Math.round(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };

    if (gameOver) {
      return formatRating(player.rankedRatingDiff || 0);
    }
    return (player.rankedMatchGamesPlayed || 0) +
      (player.rankedSitAndGoGamesPlayed || 0) <
      10
      ? 'cal.'
      : player.rankedRating;
  };

  const getJoinedPlayers = (game: Game) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const players = Object.values(game.players);
    return (
      <Stack direction="row" spacing={2} width={'100%'} mt={1}>
        <Stack>
          {players.slice(0, 5).map((p, i) => (
            <Stack direction={'row'}>
              <Tooltip title={p.username} placement="top">
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: players.length > 5 ? 80 : 160,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'pre',
                  }}
                >
                  {game.mode === 'sit-n-go' ? `${i + 1}. ` : ''}
                  {p.username}
                </Typography>
              </Tooltip>
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    {getRatingNextToUsername(game.mode, game.gameOver, p)}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          ))}
        </Stack>
        <Stack>
          {players.slice(5, 10).map((p, i) => (
            <Stack direction={'row'}>
              <Tooltip title={p.username} placement="top">
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: 80,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'pre',
                  }}
                >
                  {game.mode === 'sit-n-go' ? `${i + 6}.` : ''} {p.username}
                </Typography>
              </Tooltip>
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    {getRatingNextToUsername(game.mode, game.gameOver, p)}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  };

  const gameType = (r: Game) =>
    r.demo
      ? '--'
      : r.gameType === 'casual'
      ? 'Cas.'
      : r.gameType === 'pro'
      ? 'Pro'
      : r.gameType;

  const categoryAndDemo = (r: Game) =>
    r.demo || r.categorySet === 'Demo'
      ? 'Demo'
      : CATEGORY_SETS_LABEL[r.categorySet];

  const getStatus = (r: Game) => {
    if (r.mode === 'sit-n-go' && !r.gameStarted && r.activePlayers) {
      return `Joined: ${r.activePlayers.length} / ${r.sitAndGoMaxPlayers}`;
    }
    if (r.mode === 'teams' && !r.gameStarted && r.activePlayers) {
      return `Joined: ${r.activePlayers.length} / ${MAX_PLAYERS_IN_TEAMS}`;
    }
    return r.status;
  };

  const actionButton = (r: Game) => {
    if (r.gameOver) return null;
    if (
      r.mode === 'match' &&
      !r.activePlayers.includes(profileId) &&
      r.activePlayers.length !== 2
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            size="small"
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
        </Stack>
      );
    }
    if (
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      !r.activePlayers.includes(profileId) &&
      r.status !== 'Starting' &&
      r.activePlayers.length < r.sitAndGoMaxPlayers
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            size="small"
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
        </Stack>
      );
    }
    if (
      r.mode === 'teams' &&
      !r.gameStarted &&
      !r.activePlayers.includes(profileId) &&
      r.status !== 'Starting' &&
      r.activePlayers.length < MAX_PLAYERS_IN_TEAMS
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            size="small"
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
        </Stack>
      );
    }
    if (
      (r.mode === 'match' || r.mode === 'sit-n-go') &&
      r.activePlayers.includes(profileId) &&
      !r.gameStarted
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleLeaveGameClick(r.gameId, profileId)}
          >
            Leave Game
          </Button>
        </Stack>
      );
    }
  };

  const openGameCard = (game: Game) => (
    <Stack
      spacing={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: 300,
        height: '100%',
        paddingY: 0.5,
        paddingX: 2,
        border: '1px solid',
        borderColor: theme.palette.primary.main,
        borderRadius: 2,
      }}
    >
      <Stack>
        <Stack direction="row" mb={1}>
          <Box sx={{ width: '80%' }}>
            <Typography variant="caption">
              {MODE_DISPLAY_NAME[game.mode]} - {gameType(game)} -{' '}
              {categoryAndDemo(game)}
            </Typography>
          </Box>
          <Box sx={{ width: '20%' }}>
            {game.ranked ? (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.ranked.main }}
              >
                Ranked
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Divider />
        {getJoinedPlayers(game)}
      </Stack>
      <Stack
        direction={'row'}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: '50%' }}>
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.dailies.main }}
          >
            {getStatus(game)}
          </Typography>
        </Box>
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            alignItems: 'right',
            justifyContent: 'right',
          }}
        >
          {actionButton(game)}
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Stack direction="row">
      {openGames && openGames.length ? (
        <Stack direction="row" spacing={2} sx={{ padding: 1 }}>
          {openGames.slice(0, 2).map((og) => openGameCard(og))}
        </Stack>
      ) : (
        <Typography variant="subtitle2" mt={2}>
          No open games in the lobby
        </Typography>
      )}
    </Stack>
  );
};
